import * as radio from "@zag-js/radio-group";
import { useMachine, normalizeProps } from "@zag-js/react";
import { ReactNode, useCallback } from "react";
import { identity } from "lodash";
import { useSyncValues } from "../../hooks/use-sync-values";
import {
  getActionContainerClassNames,
  getActionTriggerClassNames,
} from "../../styles/action-trigger-styles";

export type Toggle<T> = {
  value: T;
  label: ReactNode;
};

export type ToggleGroupProps<T> = {
  id: string;
  toggles: readonly Toggle<T>[];
  onChange: (value: T) => void;
  value: T | undefined;
  label: string;
};

export function ToggleGroup<T extends string>({
  id,
  toggles,
  onChange,
  value,
  label,
}: ToggleGroupProps<T>) {
  const onValueChange: (details: { value: string }) => void = useCallback(
    ({ value }) => {
      /**
       * values are of type T, when they're rendered, therefore it is
       * safe to cast as T here.
       * tabs.machine widens the type to string
       */
      onChange(value as T);
    },
    [onChange]
  );

  const [state, send] = useMachine(radio.machine({ id, value }), {
    context: { onChange: onValueChange },
  });
  const api = radio.connect(state, send, normalizeProps);

  useSyncValues({
    parentValue: value!,
    setParentValue: onChange,
    transformParentValue: identity,
    localValue: api.value!,
    setLocalValue: api.setValue,
    transformLocalValue: identity,
  });

  return (
    <div {...api.rootProps}>
      <span className="sr-only" {...api.labelProps}>
        {label}
      </span>

      <div className={getActionContainerClassNames("small")}>
        {toggles.map(({ value, label }) => {
          const state = value === api.value ? "selected" : "unselected";

          return (
            <label
              key={value}
              {...api.getRadioProps({ value })}
              className={getActionTriggerClassNames("small", state)}
            >
              <span {...api.getRadioLabelProps({ value })}>{label}</span>
              <input {...api.getRadioInputProps({ value })} />
              <div {...api.getRadioControlProps({ value })} />
            </label>
          );
        })}
      </div>
    </div>
  );
}
